import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { COUNTRY_CODES, CountryCode } from '../../data/countryCodes';
import { useAppSelector } from '../../store/hooks';

interface CountryCodeSelectorProps {
  selectedCountry: CountryCode;
  onCountryChange: (country: CountryCode) => void;
  className?: string;
  disabled?: boolean;
  forceTheme?: 'light' | 'dark';
  dropdownZIndex?: number;
}

const CountryCodeSelector: React.FC<CountryCodeSelectorProps> = ({
  selectedCountry,
  onCountryChange,
  className = '',
  disabled = false,
  forceTheme,
  dropdownZIndex = 9999
}) => {
  const { theme: storeTheme } = useAppSelector((state) => state.ui);
  const theme = forceTheme || storeTheme;
  const [isOpen, setIsOpen] = useState(false);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCountries, setFilteredCountries] = useState(COUNTRY_CODES);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [isSearching, setIsSearching] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);


  // Filter countries based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCountries(COUNTRY_CODES);
      setIsSearching(false);
    } else {
      setIsSearching(true);
      // Simulate search delay for better UX
      const timeoutId = setTimeout(() => {
        const filtered = COUNTRY_CODES.filter(country =>
          country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          country.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
          country.dialCode.includes(searchTerm)
        );
        setFilteredCountries(filtered);
        setIsSearching(false);
      }, 150);
      
      return () => clearTimeout(timeoutId);
    }
    // Reset focus when filtering
    setFocusedIndex(-1);
  }, [searchTerm]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Recalculate position on window resize and scroll
  useEffect(() => {
    if (isOpen) {
      const handleResize = () => calculateDropdownPosition();
      const handleScroll = () => calculateDropdownPosition();
      
      window.addEventListener('resize', handleResize);
      window.addEventListener('scroll', handleScroll, true);
      
      return () => {
        window.removeEventListener('resize', handleResize);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isOpen]);

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && dropdownRef.current) {
      const focusedElement = dropdownRef.current.querySelector(`[data-index="${focusedIndex}"]`) as HTMLElement;
      if (focusedElement) {
        focusedElement.scrollIntoView({
          block: 'nearest',
          behavior: 'smooth'
        });
      }
    }
  }, [focusedIndex]);

  // Click outside handler
  useEffect(() => {
    if (isOpen) {
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as HTMLElement;
        
        // Check if click is on the button or inside the dropdown
        const isButtonClick = buttonRef.current && buttonRef.current.contains(target);
        const isDropdownClick = target.closest('[data-dropdown="country-selector"]');
        
        // Only close if click is truly outside
        if (!isButtonClick && !isDropdownClick) {
          setIsOpen(false);
          setFocusedIndex(-1);
        }
      };

      // Use a small delay to prevent immediate closure
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);


  const handleCountrySelect = (country: CountryCode) => {
    onCountryChange(country);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Calculate dropdown position
  const calculateDropdownPosition = () => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const position = {
        top: rect.bottom + 4, // Use getBoundingClientRect which is relative to viewport
        left: rect.left,
        width: Math.max(rect.width, 200) // Ensure minimum width
      };
      setDropdownPosition(position);
    }
  };

  const handleToggle = () => {
    if (!disabled) {
      if (!isOpen) {
        calculateDropdownPosition();
        setIsOpen(true);
        setFocusedIndex(-1);
      } else {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev < filteredCountries.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex(prev => 
          prev > 0 ? prev - 1 : filteredCountries.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < filteredCountries.length) {
          handleCountrySelect(filteredCountries[focusedIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchTerm('');
        setFocusedIndex(-1);
        break;
    }
  };

  return (
    <div className={`relative h-full ${className}`} ref={dropdownRef}>
      {/* Selected Country Display */}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`
          w-full h-14 px-2.5 py-0 text-left backdrop-blur-sm rounded-l-2xl
          focus:outline-none focus:border-green-500/50 focus:ring-2 focus:ring-green-500/20
          transition-all duration-300 ease-out flex items-center justify-between group
          ${theme === 'dark' 
            ? 'bg-white/5 border border-white/10 text-white hover:bg-white/10 hover:shadow-lg hover:scale-[1.02]' 
            : 'bg-white/90 border border-gray-200/50 text-gray-900 hover:bg-gray-50/90 hover:shadow-md hover:scale-[1.02]'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${isOpen ? 'ring-2 ring-green-500/20 shadow-lg' : ''}
        `}
      >
        <div className="flex items-center space-x-2 min-w-0">
          <span className="text-lg flex-shrink-0 transition-transform duration-200 group-hover:scale-110">{selectedCountry.flag}</span>
          <div className="flex flex-col min-w-0 justify-center">
            <span className={`text-sm font-semibold leading-tight ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>{selectedCountry.dialCode}</span>
            <span className={`text-[10px] leading-tight truncate ${
              theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
            }`} title={selectedCountry.name}>{selectedCountry.name}</span>
          </div>
        </div>
        <ChevronDown 
          className={`h-5 w-5 transition-all duration-300 ${
            theme === 'dark' ? 'text-gray-400 group-hover:text-white' : 'text-gray-500 group-hover:text-gray-700'
          } ${isOpen ? 'rotate-180 scale-110' : 'group-hover:scale-110'}`} 
        />
      </button>

      {/* Dropdown Menu - Portal */}
      {isOpen && createPortal(
          <div 
            data-dropdown="country-selector"
            className={`fixed rounded-2xl shadow-2xl max-h-60 overflow-hidden border transition-all duration-300 ease-out transform ${
              theme === 'dark' 
                ? 'bg-gray-800 border-gray-600 shadow-black/50' 
                : 'bg-white border-gray-300 shadow-gray-200/50'
            }`}
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              minWidth: '128px',
              zIndex: dropdownZIndex,
              position: 'fixed'
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
          {/* Search Input */}
          <div className={`p-2 border-b ${
            theme === 'dark' ? 'border-gray-600' : 'border-gray-300'
          }`}>
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder="Search countries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                className={`w-full px-3 py-2 pl-8 text-sm rounded-lg focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all duration-200 ${
                  theme === 'dark' 
                    ? 'bg-gray-700 border border-gray-500 text-white placeholder-gray-400 hover:bg-gray-600 focus:bg-gray-600' 
                    : 'bg-gray-50 border border-gray-300 text-gray-900 placeholder-gray-500 hover:bg-gray-100 focus:bg-gray-100'
                }`}
              />
              <div className="absolute left-2 top-1/2 transform -translate-y-1/2">
                {isSearching ? (
                  <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                )}
              </div>
            </div>
          </div>

          {/* Countries List with Custom Scrollbar */}
          <div className={`max-h-60 overflow-y-auto custom-scrollbar ${
            theme === 'dark' ? 'scrollbar-dark' : 'scrollbar-light'
          }`}>
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country, index) => (
                <button
                  key={country.code}
                  type="button"
                  data-index={index}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCountrySelect(country);
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCountrySelect(country);
                  }}
                  className={`
                    w-full px-3 py-2 text-left transition-all duration-200 ease-out
                    flex items-center justify-between group relative
                    active:scale-95 touch-manipulation cursor-pointer
                    pointer-events-auto z-10
                    ${theme === 'dark' 
                      ? 'hover:bg-white/10 hover:shadow-lg hover:scale-[1.02]' 
                      : 'hover:bg-gray-100/60 hover:shadow-md hover:scale-[1.02]'
                    }
                    ${selectedCountry.code === country.code 
                      ? (theme === 'dark' ? 'bg-green-500/20 border-l-4 border-green-500' : 'bg-green-100/50 border-l-4 border-green-500') 
                      : 'border-l-4 border-transparent'
                    }
                    ${focusedIndex === index 
                      ? (theme === 'dark' ? 'bg-white/10 ring-2 ring-green-500/50 shadow-lg' : 'bg-blue-100/50 ring-2 ring-blue-500/50 shadow-md') 
                      : ''
                    }
                  `}
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-base transition-transform duration-200 group-hover:scale-110">{country.flag}</span>
                    <div className="flex flex-col">
                      <span className={`text-xs font-medium ${
                        theme === 'dark' ? 'text-white' : 'text-gray-900'
                      }`}>{country.name}</span>
                      <span className={`text-xs ${
                        theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                      }`}>{country.code}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm font-mono font-semibold ${
                      theme === 'dark' ? 'text-green-400' : 'text-green-600'
                    }`}>{country.dialCode}</span>
                    {selectedCountry.code === country.code && (
                      <div className={`w-2 h-2 rounded-full ${
                        theme === 'dark' ? 'bg-green-400' : 'bg-green-600'
                      }`}></div>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className={`px-4 py-4 text-center ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-500'
              }`}>
                <div className="flex flex-col items-center space-y-2">
                  <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <p className="text-sm">No countries found</p>
                  <p className="text-xs opacity-75">Try a different search term</p>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CountryCodeSelector;
